import type { PlayerFrame } from '@realmery/realm-core';

/**
 * How the GM's window talks to the Player View (docs/PARITY-STEP-4-PLAN.md decision 4): a
 * same-origin `BroadcastChannel` in this browser, nothing through the server. The GM's
 * window sends a map as players may see it (`playerFrame`), and its picture once; the
 * Player View asks for both when it opens, and draws only what it's sent.
 */

export const PLAYER_CHANNEL = 'realmery-player-view';

export type ToPlayerView =
  | { readonly type: 'frame'; readonly frame: PlayerFrame }
  | { readonly type: 'image'; readonly key: string; readonly mimeType: string; readonly data: Uint8Array }
  /** No map is being shown: the GM closed the Player View, or the realm. */
  | { readonly type: 'closed' };

export type FromPlayerView = { readonly type: 'hello' };

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;

/** Messages are checked for their shape before they're used. */
export function toPlayerView(data: unknown): ToPlayerView | null {
  if (!isObject(data)) return null;
  if (data['type'] === 'closed') return { type: 'closed' };
  if (data['type'] === 'image' && typeof data['key'] === 'string' && typeof data['mimeType'] === 'string' && /^image\/(png|jpeg|webp)$/.test(data['mimeType']) && data['data'] instanceof Uint8Array) {
    return { type: 'image', key: data['key'], mimeType: data['mimeType'], data: data['data'] };
  }
  if (data['type'] === 'frame' && isObject(data['frame']) && typeof data['frame']['mapId'] === 'string' && Array.isArray(data['frame']['pins'])) {
    return { type: 'frame', frame: data['frame'] as unknown as PlayerFrame };
  }
  return null;
}

export const isHello = (data: unknown): boolean => isObject(data) && data['type'] === 'hello';
